/**
 * Чистая математика шкалы окон (`WindowTimeline`): даты без часовых поясов,
 * шаг перетаскивания, границы, масштаб по месяцам, перестановка строк.
 * Никакого React и DOM: всё проверяется vitest.
 *
 * Даты — ISO «ГГГГ-ММ-ДД». Окно включающее: «20 фев – 22 мар» — оба дня в окне;
 * окно без конца (`to === null`) — одна дата, рисуется меткой.
 */

export const DAY_MS = 86_400_000;

export type ShiftKind = 'move' | 'start' | 'end';

export interface DayWindow {
  from: string;
  /** `null` — окно без конца (метка в одну дату). */
  to: string | null;
}

/** День ISO-даты в миллисекундах UTC; мусор → NaN. */
export function parseIso(iso: string): number {
  const [y = NaN, m = NaN, d = NaN] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function toIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  return toIso(parseIso(iso) + days * DAY_MS);
}

/** Сколько суток от `a` до `b` (положительно, если `b` позже). */
export function diffDays(a: string, b: string): number {
  return Math.round((parseIso(b) - parseIso(a)) / DAY_MS);
}

export function maxIso(a: string, b: string): string {
  return a >= b ? a : b;
}

export function minIso(a: string, b: string): string {
  return a <= b ? a : b;
}

/** Шаг сетки перетаскивания в сутках. */
export type TimelineStep = 'day' | 'week';
export const STEP_DAYS: Record<TimelineStep, number> = { day: 1, week: 7 };

/** Пиксели → целое число шагов (в сутках), округляя к ближайшему. */
export function snapDelta(pixels: number, dayPx: number, step: TimelineStep = 'day'): number {
  if (!Number.isFinite(pixels) || !(dayPx > 0)) return 0;
  const stepDays = STEP_DAYS[step];
  const snapped = Math.round(pixels / dayPx / stepDays) * stepDays;
  return snapped === 0 ? 0 : snapped; // без «-0»
}

export interface DragBounds {
  /** Самая ранняя допустимая дата начала. */
  min?: string;
  /** Самая поздняя допустимая дата конца. */
  max?: string;
}

/**
 * Окно после сдвига на `delta` суток.
 *
 * - `move` — оба края вместе; упирается в границы целиком, длительность не меняется.
 * - `start` — двигается только начало; не позже конца (окно не короче суток).
 * - `end` — двигается только конец; не раньше начала.
 *
 * У окна без конца края менять нельзя: возвращается оно же.
 */
export function dragWindow(kind: ShiftKind, win: DayWindow, delta: number, bounds: DragBounds = {}): DayWindow {
  const { from, to } = win;
  if (kind === 'move') {
    let d = delta;
    const last = to ?? from;
    if (bounds.min !== undefined) d = Math.max(d, diffDays(from, bounds.min));
    if (bounds.max !== undefined) d = Math.min(d, diffDays(last, bounds.max));
    if (d === 0) return { from, to };
    return { from: addDays(from, d), to: to === null ? null : addDays(to, d) };
  }
  if (to === null) return { from, to };
  if (kind === 'start') {
    let next = addDays(from, delta);
    if (bounds.min !== undefined) next = maxIso(next, bounds.min);
    next = minIso(next, to);
    return { from: next, to };
  }
  let next = addDays(to, delta);
  if (bounds.max !== undefined) next = minIso(next, bounds.max);
  next = maxIso(next, from);
  return { from, to: next };
}

export function sameWindow(a: DayWindow, b: DayWindow): boolean {
  return a.from === b.from && a.to === b.to;
}

/* ------------------------------------------------------------------ масштаб */

export interface TimelineScale {
  /** Первый день шкалы (начало месяца). */
  start: string;
  /** День после последнего (начало следующего месяца). */
  end: string;
  totalDays: number;
  /** Начала месяцев: подпись рисует вызывающий. */
  ticks: { iso: string; month: number; year: number; left: number }[];
}

function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

function nextMonthStart(iso: string): string {
  const [y = 0, m = 1] = iso.split('-').map(Number);
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
}

/**
 * Шкала по месяцам, охватывающая все окна с запасом `slackDays`
 * (чтобы крайнюю работу можно было сдвинуть наружу). Явный `range` задаёт
 * шкалу точно (с округлением до месяцев).
 */
export function buildScale(windows: DayWindow[], opts: { slackDays?: number; range?: { from: string; to: string } } = {}): TimelineScale | null {
  let lo: string | null = null;
  let hi: string | null = null;
  if (opts.range) {
    lo = opts.range.from;
    hi = opts.range.to;
  } else {
    const slack = opts.slackDays ?? 14;
    for (const w of windows) {
      const last = w.to ?? w.from;
      if (lo === null || w.from < lo) lo = w.from;
      if (hi === null || last > hi) hi = last;
    }
    if (lo === null || hi === null) return null;
    lo = addDays(lo, -slack);
    hi = addDays(hi, slack);
  }
  if (lo === null || hi === null) return null;
  const start = monthStart(lo);
  const end = nextMonthStart(hi);
  const totalDays = diffDays(start, end);
  const ticks: TimelineScale['ticks'] = [];
  for (let cursor = start; cursor < end; cursor = nextMonthStart(cursor)) {
    const [y = 0, m = 1] = cursor.split('-').map(Number);
    ticks.push({ iso: cursor, month: m, year: y, left: (diffDays(start, cursor) / totalDays) * 100 });
  }
  return { start, end, totalDays, ticks };
}

/** Доля ширины шкалы (0–100), с которой начинается день `iso`. */
export function pctOf(scale: TimelineScale, iso: string): number {
  return (diffDays(scale.start, iso) / scale.totalDays) * 100;
}

/** Доля ширины, которую занимает окно (включающее; метка — не меньше 0,4 %). */
export function widthPctOf(scale: TimelineScale, win: DayWindow): number {
  const days = win.to === null ? 1 : diffDays(win.from, win.to) + 1;
  return Math.max((days / scale.totalDays) * 100, 0.4);
}

/* -------------------------------------------------------------- перестановка */

export interface OrderedGroup {
  id: string;
  ids: string[];
}

export interface RowMove {
  id: string;
  fromGroupId: string;
  toGroupId: string;
  /** Место среди строк целевой группы ПОСЛЕ того, как перетаскиваемая вынута. */
  index: number;
}

/** Применяет перенос к порядку групп (неизменяемо). Неизвестные id — без изменений. */
export function applyRowMove(groups: OrderedGroup[], move: RowMove): OrderedGroup[] {
  const source = groups.find((g) => g.ids.includes(move.id));
  const target = groups.find((g) => g.id === move.toGroupId);
  if (!source || !target) return groups;
  return groups.map((group) => {
    let ids = group.ids.filter((id) => id !== move.id);
    if (group.id === target.id) {
      const at = Math.max(0, Math.min(move.index, ids.length));
      ids = [...ids.slice(0, at), move.id, ...ids.slice(at)];
    } else if (group.id !== source.id) {
      return group;
    }
    return { ...group, ids };
  });
}

/** Перенос на шаг клавишей: вверх/вниз внутри группы, на краю — в соседнюю. `null` — некуда. */
export function keyboardMove(groups: OrderedGroup[], id: string, direction: -1 | 1): RowMove | null {
  const gi = groups.findIndex((g) => g.ids.includes(id));
  if (gi < 0) return null;
  const group = groups[gi]!;
  const at = group.ids.indexOf(id);
  const inner = at + direction;
  if (inner >= 0 && inner < group.ids.length) {
    return { id, fromGroupId: group.id, toGroupId: group.id, index: inner };
  }
  const neighbour = groups[gi + direction];
  if (!neighbour) return null;
  return { id, fromGroupId: group.id, toGroupId: neighbour.id, index: direction === -1 ? neighbour.ids.length : 0 };
}

export function sameOrder(a: OrderedGroup[], b: OrderedGroup[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((g, i) => g.id === b[i]!.id && g.ids.length === b[i]!.ids.length && g.ids.every((id, j) => id === b[i]!.ids[j]));
}
